import { useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AsciiCharField from './AsciiCharField';
import LFDColour from './LFDColour';
import LinkedDevicesConfig, { LinkedDevice, linkedDevicesToJson, parseLinkedDevices } from './LinkedDevicesConfig';
import PortConfigComponent, { buildDefaultPortConfig, PortConfig } from './PortConfig';

// Mirrors Weightrax.Domain.Enums.VehicleTrackingEvent (integer-serialized)
export const VehicleTrackingEvent = {
  VehicleArrived: 0,
  VehicleOnBridge: 1,
  WeightChanged: 2,
  WeightCaptured: 3,
  AxleChanging: 4,
  VehicleOffBridge: 5,
  VehicleDeparted: 6,
  Idle: 7
} as const;

type VehicleTrackingEventValue = (typeof VehicleTrackingEvent)[keyof typeof VehicleTrackingEvent];

const ALL_VEHICLE_TRACKING_EVENTS: { value: VehicleTrackingEventValue; label: string }[] = [
  // Shown from start-up until a vehicle arrives and again once it has departed, so the display is never left without data.
  { value: VehicleTrackingEvent.Idle, label: 'Idle (default)' },
  { value: VehicleTrackingEvent.VehicleArrived, label: 'Vehicle Arrived' },
  { value: VehicleTrackingEvent.VehicleOnBridge, label: 'Vehicle On Bridge' },
  { value: VehicleTrackingEvent.WeightChanged, label: 'Weight Changed' },
  { value: VehicleTrackingEvent.WeightCaptured, label: 'Weight Captured' },
  { value: VehicleTrackingEvent.AxleChanging, label: 'Axle Changing' },
  { value: VehicleTrackingEvent.VehicleOffBridge, label: 'Vehicle Off Bridge' },
  { value: VehicleTrackingEvent.VehicleDeparted, label: 'Vehicle Departed' }
];

// Sensible starting templates so a newly enabled subscription isn't silently blank; events not
// listed here default to null (which clears the display, e.g. on departure).
const DEFAULT_MESSAGE_TEMPLATES: Partial<Record<VehicleTrackingEventValue, string>> = {
  [VehicleTrackingEvent.Idle]: 'READY',
  [VehicleTrackingEvent.VehicleArrived]: '{REGO}',
  [VehicleTrackingEvent.WeightChanged]: '{WEIGHT} {UNIT}',
  [VehicleTrackingEvent.WeightCaptured]: '{WEIGHT} {UNIT}'
};

// Mirrors Weightrax.Domain.Enums.Alignment (integer-serialized)
export const Alignment = {
  Left: 0,
  Center: 1,
  Right: 2
} as const;

type AlignmentValue = (typeof Alignment)[keyof typeof Alignment];

const ALIGNMENT_OPTIONS: { value: AlignmentValue; label: string }[] = [
  { value: Alignment.Left, label: 'Left' },
  { value: Alignment.Center, label: 'Center' },
  { value: Alignment.Right, label: 'Right' }
];

export type LfdEventSubscription = {
  event: VehicleTrackingEventValue;
  messageTemplate: string | null;
  colour: string[];
  alignment: AlignmentValue;
  messageRepeatSeconds: number;
  displaySeconds: number;
  messageRepeatTimes: number;
  timeoutSeconds: number;
};

export type LfdProtocolFormat = {
  weight: { location: number; length: number; zero: string };
  color: { location: number; green: string[]; red: string[]; yellow: string[] };
  frame: { start: number; length: number };
};

export type LfdProtocol = {
  id: number;
  name: string;
  skip_status_slot: boolean;
  suppress_unit_symbol: boolean;
  format: LfdProtocolFormat;
};

export type LfdConfig = {
  name: string;
  portConfig: PortConfig;
  protocol: LfdProtocol;
  subscriptions: LfdEventSubscription[];
  linkedDevices: LinkedDevice[];
  /** The weight indicator this LFD shows; null is the first indicator on the device. */
  indicatorPeripheralId: string | null;
  /** The indicator's platform (configured scale, by position); null is the first. */
  scaleIndex: number | null;
};

/** An indicator an LFD can be linked to, with the platforms it is configured with. */
export type LfdIndicatorOption = {
  peripheralId: string;
  name: string;
  /** Platform (scale) names, in configured order — the position is what the LFD stores. */
  platforms: string[];
};

type LfdPeripheralConfigProps = {
  value: LfdConfig;
  onChange: (next: LfdConfig) => void;
  /** The site's indicators, for choosing which weight feed this LFD shows. */
  indicators?: LfdIndicatorOption[];
  disabled?: boolean;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asStringArray(value: unknown, fallback: string[] = []): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  return fallback;
}

/** Blank reads as no indicator: the id is a Ulid on the device, and an empty one would not deserialise. */
function asIdOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function asIndexOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/**
 * The platform (scale) names an indicator is configured with, in order, read from its stored
 * configuration — which may be in either casing, as configurations saved on a device are PascalCase.
 */
export function readIndicatorPlatforms(configuration: string | null | undefined): string[] {
  if (!configuration) return [];

  try {
    const source = asRecord(JSON.parse(configuration));
    const weightConfigs = source.weightConfigs ?? source.WeightConfigs;
    if (!Array.isArray(weightConfigs)) return [];

    return weightConfigs.map((item) => {
      const rec = asRecord(item);
      return asString(rec.name ?? rec.Name);
    });
  } catch {
    return [];
  }
}

function buildProtocol(source: Record<string, unknown>): LfdProtocol {
  const format = asRecord(source.format);
  const weight = asRecord(format.weight);
  const color = asRecord(format.color);
  const frame = asRecord(format.frame);

  return {
    id: asNumber(source.id, 7),
    name: asString(source.name, 'LFD Protocol 1.0'),
    skip_status_slot: asBoolean(source.skip_status_slot, true),
    suppress_unit_symbol: asBoolean(source.suppress_unit_symbol, true),
    format: {
      weight: {
        location: asNumber(weight.location, 1),
        length: asNumber(weight.length, 8),
        zero: asString(weight.zero, '-------')
      },
      color: {
        location: asNumber(color.location, 9),
        green: asStringArray(color.green, ['g']),
        red: asStringArray(color.red, ['r']),
        yellow: asStringArray(color.yellow, ['y'])
      },
      frame: {
        start: asNumber(frame.start, 2),
        length: asNumber(frame.length, 10)
      }
    }
  };
}

function buildSubscription(source: unknown): LfdEventSubscription {
  const rec = asRecord(source);
  return {
    event: asNumber(rec.event, VehicleTrackingEvent.VehicleArrived) as VehicleTrackingEventValue,
    messageTemplate: typeof rec.messageTemplate === 'string' ? rec.messageTemplate : null,
    colour: asStringArray(rec.colour, ['g']),
    alignment: asNumber(rec.alignment, Alignment.Center) as AlignmentValue,
    messageRepeatSeconds: asNumber(rec.messageRepeatSeconds, 5),
    displaySeconds: asNumber(rec.displaySeconds, 1),
    messageRepeatTimes: asNumber(rec.messageRepeatTimes, 1),
    timeoutSeconds: asNumber(rec.timeoutSeconds, 0)
  };
}

function buildDefaultSubscriptions(): LfdEventSubscription[] {
  return ALL_VEHICLE_TRACKING_EVENTS.map(({ value: evt }) => ({
    event: evt,
    messageTemplate: DEFAULT_MESSAGE_TEMPLATES[evt] ?? null,
    colour: ['g'],
    alignment: Alignment.Center,
    messageRepeatSeconds: 5,
    displaySeconds: 1,
    messageRepeatTimes: 1,
    timeoutSeconds: 0
  }));
}

export function buildDefaultLfdConfig(base?: unknown): LfdConfig {
  const source = asRecord(base);

  const subscriptions = Array.isArray(source.subscriptions) ? source.subscriptions.map(buildSubscription) : buildDefaultSubscriptions();

  return {
    name: asString(source.name, ''),
    portConfig: buildDefaultPortConfig(source.portConfig),
    protocol: buildProtocol(asRecord(source.protocol)),
    subscriptions,
    linkedDevices: parseLinkedDevices(source),
    indicatorPeripheralId: asIdOrNull(source.indicatorPeripheralId ?? source.IndicatorPeripheralId),
    scaleIndex: asIndexOrNull(source.scaleIndex ?? source.ScaleIndex)
  };
}

function toJson(value: LfdConfig): unknown {
  return {
    name: value.name,
    portConfig: value.portConfig,
    subscriptions: value.subscriptions,
    linkedDevices: linkedDevicesToJson(value.linkedDevices),
    indicatorPeripheralId: value.indicatorPeripheralId,
    scaleIndex: value.scaleIndex,
    protocol: {
      id: value.protocol.id,
      name: value.protocol.name,
      skip_status_slot: value.protocol.skip_status_slot,
      suppress_unit_symbol: value.protocol.suppress_unit_symbol,
      format: {
        weight: {
          location: value.protocol.format.weight.location,
          length: value.protocol.format.weight.length,
          zero: value.protocol.format.weight.zero
        },
        color: {
          location: value.protocol.format.color.location,
          green: value.protocol.format.color.green,
          red: value.protocol.format.color.red,
          yellow: value.protocol.format.color.yellow
        },
        frame: {
          start: value.protocol.format.frame.start,
          length: value.protocol.format.frame.length
        }
      }
    }
  };
}

function parseColorCodes(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function LFDPeripheralConfig({ value, onChange, indicators = [], disabled = false }: LfdPeripheralConfigProps) {
  const [advancedMode, setAdvancedMode] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const advancedJson = useMemo(() => JSON.stringify(toJson(value), null, 2), [value]);
  const [jsonText, setJsonText] = useState(advancedJson);

  const [greenText, setGreenText] = useState(() => value.protocol.format.color.green.join(', '));
  const [redText, setRedText] = useState(() => value.protocol.format.color.red.join(', '));
  const [yellowText, setYellowText] = useState(() => value.protocol.format.color.yellow.join(', '));

  const handleModeToggle = (checked: boolean) => {
    setAdvancedMode(checked);
    setJsonError(null);
    if (checked) {
      setJsonText(advancedJson);
    } else {
      setGreenText(value.protocol.format.color.green.join(', '));
      setRedText(value.protocol.format.color.red.join(', '));
      setYellowText(value.protocol.format.color.yellow.join(', '));
    }
  };

  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      onChange(buildDefaultLfdConfig(parsed));
      setJsonError(null);
    } catch {
      setJsonError('Configuration JSON is invalid. Fix the JSON and try again.');
    }
  };

  const updateProtocol = (patch: Partial<LfdProtocol>) => {
    onChange({ ...value, protocol: { ...value.protocol, ...patch } });
  };

  const updateFormat = (patch: Partial<LfdProtocolFormat>) => {
    updateProtocol({ format: { ...value.protocol.format, ...patch } });
  };

  const updateSubscription = (evt: VehicleTrackingEventValue, patch: Partial<LfdEventSubscription>) => {
    onChange({
      ...value,
      subscriptions: value.subscriptions.map((s) => (s.event === evt ? { ...s, ...patch } : s))
    });
  };

  const selectedIndicator = indicators.find((indicator) => indicator.peripheralId === value.indicatorPeripheralId);
  // An indicator that is inactive, on another site or since removed is still shown, so the link can be seen and changed.
  const isUnknownIndicator = value.indicatorPeripheralId != null && selectedIndicator === undefined;
  const isUnknownPlatform =
    value.scaleIndex != null && (selectedIndicator === undefined || value.scaleIndex >= selectedIndicator.platforms.length);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          LFD Configuration
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Simple
          </Typography>
          <Switch checked={advancedMode} onChange={(_, checked) => handleModeToggle(checked)} disabled={disabled} />
          <Typography variant="body2" color="text.secondary">
            Advanced
          </Typography>
        </Box>
      </Box>

      {!advancedMode ? (
        <Stack spacing={2.5}>
          <PortConfigComponent value={value.portConfig} onChange={(next) => onChange({ ...value, portConfig: next })} disabled={disabled} />

          <Divider />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Weight Feed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The indicator and platform this LFD shows, whose vehicle tracking drives it. Leave on the default to use the first indicator
                on the device.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                select
                label="Indicator"
                value={value.indicatorPeripheralId ?? ''}
                // A platform belongs to one indicator, so choosing another starts again from its first.
                onChange={(e) => onChange({ ...value, indicatorPeripheralId: e.target.value || null, scaleIndex: null })}
                helperText={indicators.length === 0 ? 'No active indicators on this site' : undefined}
                disabled={disabled}
              >
                <MenuItem value="">
                  <em>Default — first indicator on the device</em>
                </MenuItem>
                {isUnknownIndicator ? (
                  <MenuItem value={value.indicatorPeripheralId ?? ''}>Unknown indicator ({value.indicatorPeripheralId})</MenuItem>
                ) : null}
                {indicators.map((indicator) => (
                  <MenuItem key={indicator.peripheralId} value={indicator.peripheralId}>
                    {indicator.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Platform"
                value={value.scaleIndex ?? ''}
                onChange={(e) => onChange({ ...value, scaleIndex: e.target.value === '' ? null : Number(e.target.value) })}
                helperText={
                  selectedIndicator && selectedIndicator.platforms.length === 0 ? 'This indicator has no platforms configured' : undefined
                }
                disabled={disabled || value.indicatorPeripheralId == null}
              >
                <MenuItem value="">
                  <em>First platform</em>
                </MenuItem>
                {isUnknownPlatform && value.scaleIndex != null ? (
                  <MenuItem value={value.scaleIndex}>Unknown platform ({value.scaleIndex + 1})</MenuItem>
                ) : null}
                {(selectedIndicator?.platforms ?? []).map((platform, index) => (
                  <MenuItem key={index} value={index}>
                    {platform || `Platform ${index + 1}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Event Subscriptions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select which vehicle tracking events this LFD responds to and configure the display per event.
              </Typography>
            </Grid>

            {ALL_VEHICLE_TRACKING_EVENTS.map(({ value: evt, label }) => {
              const sub = value.subscriptions.find((s) => s.event === evt);
              const isEnabled = sub !== undefined;
              return (
                <Grid size={{ xs: 12 }} key={evt}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid size={{ xs: 'auto' }}>
                      <Checkbox
                        checked={isEnabled}
                        onChange={(_, checked) => {
                          if (checked) {
                            const newSub: LfdEventSubscription = {
                              event: evt,
                              messageTemplate: DEFAULT_MESSAGE_TEMPLATES[evt] ?? null,
                              colour: ['g'],
                              alignment: Alignment.Center,
                              // Same default as buildSubscription/buildDefaultSubscriptions, so a
                              // subscription enabled here behaves like one loaded from config.
                              messageRepeatSeconds: 5,
                              displaySeconds: 1,
                              messageRepeatTimes: 1,
                              timeoutSeconds: 0
                            };
                            onChange({ ...value, subscriptions: [...value.subscriptions, newSub] });
                          } else {
                            onChange({ ...value, subscriptions: value.subscriptions.filter((s) => s.event !== evt) });
                          }
                        }}
                        disabled={disabled}
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 4, md: 1.5 }}>
                      <Typography variant="body2" color={isEnabled ? 'text.primary' : 'text.disabled'}>
                        {label}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4, md: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Template"
                        placeholder="{WEIGHT} {UNIT}"
                        value={sub?.messageTemplate ?? ''}
                        onChange={(e) => updateSubscription(evt, { messageTemplate: e.target.value || null })}
                        disabled={disabled || !isEnabled}
                      />
                    </Grid>
                    <Grid size={{ xs: 4, md: 1.5 }}>
                      {isEnabled && (
                        <LFDColour
                          value={sub.colour}
                          onChange={(next) => updateSubscription(evt, { colour: next })}
                          disabled={disabled}
                          size="small"
                        />
                      )}
                    </Grid>
                    <Grid size={{ xs: 4, md: 1.5 }}>
                      {isEnabled && (
                        <FormControl fullWidth size="small" disabled={disabled}>
                          <InputLabel>Alignment</InputLabel>
                          <Select
                            label="Alignment"
                            value={sub.alignment}
                            onChange={(e) => updateSubscription(evt, { alignment: e.target.value as AlignmentValue })}
                          >
                            {ALIGNMENT_OPTIONS.map(({ value: align, label: alignLabel }) => (
                              <MenuItem key={align} value={align}>
                                {alignLabel}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Grid>
                    <Grid size={{ xs: 3, md: 1.2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Message Repeat (s)"
                        value={isEnabled ? (sub.messageRepeatSeconds ?? '') : ''}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n)) updateSubscription(evt, { messageRepeatSeconds: n });
                        }}
                        disabled={disabled || !isEnabled}
                      />
                    </Grid>
                    <Grid size={{ xs: 3, md: 1.2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Display (s)"
                        value={isEnabled ? (sub.displaySeconds ?? '') : ''}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n)) updateSubscription(evt, { displaySeconds: n });
                        }}
                        disabled={disabled || !isEnabled}
                      />
                    </Grid>
                    <Grid size={{ xs: 3, md: 1.2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Repeat Times"
                        value={isEnabled ? (sub.messageRepeatTimes ?? '') : ''}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n)) updateSubscription(evt, { messageRepeatTimes: n });
                        }}
                        disabled={disabled || !isEnabled}
                      />
                    </Grid>
                    <Grid size={{ xs: 3, md: 1.2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Timeout (s)"
                        title="0 = indefinite"
                        value={isEnabled ? (sub.timeoutSeconds ?? '') : ''}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n)) updateSubscription(evt, { timeoutSeconds: n });
                        }}
                        disabled={disabled || !isEnabled}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>

          <Divider />

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Protocol
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Protocol ID"
                value={value.protocol.id}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) updateProtocol({ id: n });
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label="Protocol Name"
                value={value.protocol.name}
                onChange={(e) => updateProtocol({ name: e.target.value })}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={value.protocol.skip_status_slot}
                    onChange={(_, checked) => updateProtocol({ skip_status_slot: checked })}
                    disabled={disabled}
                  />
                }
                label="Skip Status Slot"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={value.protocol.suppress_unit_symbol}
                    onChange={(_, checked) => updateProtocol({ suppress_unit_symbol: checked })}
                    disabled={disabled}
                  />
                }
                label="Suppress Unit Symbol"
              />
            </Grid>
          </Grid>

          <Divider />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Format — Weight
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Location"
                value={value.protocol.format.weight.location}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) updateFormat({ weight: { ...value.protocol.format.weight, location: n } });
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Length"
                value={value.protocol.format.weight.length}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) updateFormat({ weight: { ...value.protocol.format.weight, length: n } });
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Zero Placeholder"
                helperText="String displayed when weight is zero."
                value={value.protocol.format.weight.zero}
                onChange={(e) => updateFormat({ weight: { ...value.protocol.format.weight, zero: e.target.value } })}
                disabled={disabled}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Format — Color
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Location"
                value={value.protocol.format.color.location}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) updateFormat({ color: { ...value.protocol.format.color, location: n } });
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Green Codes"
                helperText="Comma-separated."
                value={greenText}
                onChange={(e) => setGreenText(e.target.value)}
                onBlur={() => updateFormat({ color: { ...value.protocol.format.color, green: parseColorCodes(greenText) } })}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Red Codes"
                helperText="Comma-separated."
                value={redText}
                onChange={(e) => setRedText(e.target.value)}
                onBlur={() => updateFormat({ color: { ...value.protocol.format.color, red: parseColorCodes(redText) } })}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Yellow Codes"
                helperText="Comma-separated."
                value={yellowText}
                onChange={(e) => setYellowText(e.target.value)}
                onBlur={() => updateFormat({ color: { ...value.protocol.format.color, yellow: parseColorCodes(yellowText) } })}
                disabled={disabled}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Format — Frame
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <AsciiCharField
                label="Start"
                value={value.protocol.format.frame.start}
                onChange={(n) => {
                  if (n !== null) updateFormat({ frame: { ...value.protocol.format.frame, start: n } });
                }}
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Length"
                value={value.protocol.format.frame.length}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) updateFormat({ frame: { ...value.protocol.format.frame, length: n } });
                }}
                disabled={disabled}
              />
            </Grid>
          </Grid>

          <Divider />

          <LinkedDevicesConfig
            value={value.linkedDevices}
            onChange={(next) => onChange({ ...value, linkedDevices: next })}
            disabled={disabled}
          />
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <TextField
            fullWidth
            multiline
            minRows={18}
            label="Configuration JSON"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            disabled={disabled}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleApplyJson} disabled={disabled}>
              Apply JSON
            </Button>
          </Box>
          {jsonError ? <Alert severity="error">{jsonError}</Alert> : null}
        </Stack>
      )}
    </Stack>
  );
}
